// src/services/openai.js

/**
 * Maneja las llamadas a la API de OpenAI
 * Usa variables de entorno para la clave de API
 */

// Obtener la clave API de las variables de entorno
const API_KEY = import.meta.env.VITE_OPENAI_API_KEY || '';

/**
 * Llama a la API de OpenAI para generar respuestas
 * @param {string} prompt - El prompt para la IA
 * @param {string} systemContent - El mensaje de sistema que define el comportamiento
 * @returns {Promise<string>} - La respuesta generada
 */
export async function callOpenAI(prompt, systemContent) {
  // Si no hay API key, devolver mensaje de error
  if (!API_KEY) {
    console.error("No API key found in environment variables");
    return "❌ Error: API key not configured. Please add VITE_OPENAI_API_KEY to your .env file.";
  }

  try {
    const res = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${API_KEY}`
      },
      body: JSON.stringify({
        model: "gpt-3.5-turbo",
        messages: [
          { role: "system", content: systemContent },
          { role: "user", content: prompt }
        ]
      })
    });

    const data = await res.json();
    
    // Manejar errores de la API
    if (data.error) {
      console.error("OpenAI API error:", data.error);
      return `❌ Error: ${data.error.message || "Unknown error from API"}`;
    }
    
    // Extraer contenido de la respuesta
    if (data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) {
      return data.choices[0].message.content;
    } else {
      return "❌ Error: No response content from AI.";
    }
  } catch (error) {
    console.error("OpenAI API error:", error);
    return "❌ Error connecting to AI service. Please try again later.";
  }
}

/**
 * Función específica para generar mensaje del ministro
 * @returns {Promise<string>} Mensaje generado
 */
export async function buildMinisterMessage() {
  try {
    // Simular obtención de noticias (en producción, usaríamos una API real)
    const mockNews = [
      "Bitcoin surges to new all-time high as institutional adoption accelerates.",
      "Ethereum completes major upgrade, reducing gas fees by 90%.",
      "New regulation framework proposed for DeFi platforms.",
      "Major banks announce blockchain-based settlement system."
    ];
    
    const prompt = `You are a crypto News AI agent. Based on the following headlines, write a brief summary in English to inform the user of the latest crypto news:\n\n${mockNews.join('\n')}`;
    
    const systemContent = "You are a crypto-savvy AI minister who summarizes crypto news with clarity and confidence.";
    
    return await callOpenAI(prompt, systemContent);
  } catch (error) {
    console.error("Minister message error:", error);
    return "Minister is currently gathering intel from the crypto verse. Check back soon.";
  }
}

/**
 * Función específica para generar mensaje del soldado
 * @param {Array} tokens - Array de tokens y balances
 * @returns {Promise<string>} Mensaje generado
 */
export async function buildSoldierMessage(tokens) {
  const prompt = `You are a cheerful Balance wallet AI agent. Write a short military-style message in English, reporting how many tokens I currently own and making a brief comment on the type or trend of my recent purchases. Tokens: ${tokens.join(", ")}`;
  
  const systemContent = "You are an AI military agent that reports token stats to the user in a motivational way.";
  
  return await callOpenAI(prompt, systemContent);
}

/**
 * Función específica para generar mensaje de Cygaar
 * @returns {Promise<string>} Mensaje generado
 */
export async function buildCygaarMessage() {
  const prompt = `Write a short, uplifting message that will make the user's day better. The message should be royal, inspiring, and positive, but always related to the world of crypto, blockchain, or digital assets. Use crypto metaphors, references, or encouragement for success in the crypto space. Keep messages concise and under 20 words. Always end your message with @0xCygaar.`;
  
  const systemContent = "You are a wise and motivational AI called Cygaar who inspires users with royal wisdom and positivity, always related to crypto. Keep messages concise and under 20 words. Always end your message with your Twitter handle @0xCygaar.";
  
  return await callOpenAI(prompt, systemContent);
}

/**
 * Función específica para analizar una wallet
 * @param {string} targetWallet - Dirección de la wallet a analizar
 * @returns {Promise<string>} Análisis generado
 */
export async function buildInspectorMessage(targetWallet) {
  try {
    // En producción, aquí consultaríamos una API real de blockchain
    // Para demo, creamos datos simulados
    const mockTxs = [
      {
        hash: "0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef",
        from: targetWallet,
        to: "0xabcdef1234567890abcdef1234567890abcdef1234",
        value: "1000000000000000000", // 1 ETH
        timeStamp: Math.floor(Date.now() / 1000) - 86400 // 1 día atrás
      },
      {
        hash: "0xabcdef1234567890abcdef1234567890abcdef1234567890abcdef1234567890",
        from: "0x9876543210fedcba9876543210fedcba98765432",
        to: targetWallet,
        value: "500000000000000000", // 0.5 ETH
        timeStamp: Math.floor(Date.now() / 1000) - 172800 // 2 días atrás
      },
      {
        hash: "0xfedcba9876543210fedcba9876543210fedcba9876543210fedcba9876543210",
        from: targetWallet,
        to: "0x1234567890abcdef1234567890abcdef12345678",
        value: "2000000000000000000", // 2 ETH
        timeStamp: Math.floor(Date.now() / 1000) - 259200 // 3 días atrás
      }
    ];
    
    const txsText = mockTxs.map(tx => {
      return `Hash: ${tx.hash}\nFrom: ${tx.from}\nTo: ${tx.to}\nValue: ${parseFloat(tx.value) / 1e18} ETH\nDate: ${new Date(tx.timeStamp * 1000).toLocaleString()}`;
    }).join('\n\n');
    
    const prompt = `Summarize the following wallet activity in a friendly way for a user:\n\n${txsText}`;
    
    const systemContent = "You are a friendly blockchain inspector bot analyzing transaction patterns and wallet activities.";
    
    return await callOpenAI(prompt, systemContent);
  } catch (error) {
    console.error("Inspector error:", error);
    return "❌ Error fetching wallet info. Please try again later.";
  }
}

/**
 * Función específica para analizar un contrato
 * @param {string} contractAddress - Dirección del contrato a analizar
 * @returns {Promise<string>} Análisis generado
 */
export async function buildAnalyzerMessage(contractAddress) {
  try {
    // En producción, aquí consultaríamos una API real de contratos
    // Para demo, usamos datos simulados
    const mockContract = {
      name: "SampleToken",
      compiler: "0.8.10",
      code: `
// SPDX-License-Identifier: MIT
pragma solidity ^0.8.10;

import "@openzeppelin/contracts/token/ERC20/ERC20.sol";
import "@openzeppelin/contracts/access/Ownable.sol";

contract SampleToken is ERC20, Ownable {
    uint256 public buyTax = 5;
    uint256 public sellTax = 5;
    address public taxWallet;

    constructor() ERC20("Sample Token", "SAMPLE") {
        _mint(msg.sender, 1000000 * 10 ** decimals());
        taxWallet = msg.sender;
    }

    function setTaxes(uint256 _buyTax, uint256 _sellTax) external onlyOwner {
        require(_buyTax <= 20 && _sellTax <= 20, "Tax too high");
        buyTax = _buyTax;
        sellTax = _sellTax;
    }

    function setTaxWallet(address _taxWallet) external onlyOwner {
        taxWallet = _taxWallet;
    }
}
      `
    };
    
    const prompt = `Analyze this smart contract source code for potential security risks, rugpull risks, or suspicious functions. Focus on:
1. Hidden mint functions
2. Unrestricted transfer functions
3. Blacklist/whitelist functions
4. Owner privileges
5. Hidden fees or taxes
6. Suspicious ownership transfers

Contract Name: ${mockContract.name}
Compiler Version: ${mockContract.compiler}
Source Code: ${mockContract.code}

Provide a brief analysis (max 100 words) focusing on the most critical risks found.`;
    
    const systemContent = "You are a smart contract security expert analyzing contracts for potential risks and rugpull possibilities.";
    
    return await callOpenAI(prompt, systemContent);
  } catch (error) {
    console.error("Analyzer error:", error);
    return "❌ Error analyzing contract. Please try again later.";
  }
}

/**
 * Función para interactuar con Rose, el asistente de IA
 * @param {string} message - Mensaje del usuario
 * @returns {Promise<string>} Respuesta generada
 */
export async function getRoseResponse(message) {
  const systemContent = "You are Rose, a friendly and helpful AI companion focused on crypto topics. Keep responses concise and engaging.";
  return await callOpenAI(message, systemContent);
}